import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { MarketingLayout } from "@/components/marketing/MarketingLayout";
import { PageSeo } from "@/components/seo/PageSeo";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useOrg } from "@/contexts/OrgContext";
import { cn } from "@/lib/utils";
import { LEGAL_ENTITY } from "@/config/legal";

/**
 * Shared building blocks for the legal pages (Privacy Policy, Terms and
 * Conditions, Cookie Policy). The page renders inside the marketing chrome on
 * the apex site and inside the tenant's themed chrome on a business's website.
 */

function LegalChrome({ children }: { children: ReactNode }) {
  const { isMarketingSite } = useOrg();
  if (isMarketingSite) return <MarketingLayout>{children}</MarketingLayout>;
  return <Layout>{children}</Layout>;
}

export interface LegalTocItem {
  id: string;
  heading: string;
}

export function LegalDocument({
  slug,
  title,
  intro,
  summary,
  toc,
  children,
}: {
  slug: string;
  title: string;
  /** One-sentence description, also used as the SEO description fallback. */
  intro: string;
  /** Short plain-English summary shown at the top. */
  summary?: ReactNode;
  toc?: LegalTocItem[];
  children: ReactNode;
}) {
  // On the marketing site MarketingLayout's MarketingSeo owns the head tags.
  const { isMarketingSite } = useOrg();
  return (
    <LegalChrome>
      {!isMarketingSite && <PageSeo pageSlug={slug} fallbackTitle={title} fallbackDescription={intro} />}
      <article className="container mx-auto max-w-3xl px-4 py-16 md:py-20" data-testid={`legal-${slug}`}>
        <header className="mb-10">
          <h1 className="font-display text-4xl md:text-5xl">{title}</h1>
          <p className="mt-3 text-sm text-muted-foreground">Last updated: {LEGAL_ENTITY.lastUpdated}</p>
          <p className="mt-6 text-lg text-muted-foreground">{intro}</p>
          {summary && (
            <div className="mt-8 rounded-xl border border-border bg-muted/40 p-5 md:p-6" data-testid="legal-summary">
              <h2 className="font-display text-xl">The short version</h2>
              <div className="mt-2">{summary}</div>
            </div>
          )}
          {toc && toc.length > 0 && (
            <nav aria-label="Contents" className="mt-8">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Contents</h2>
              <ol className="mt-3 grid gap-x-6 gap-y-1.5 text-sm sm:grid-cols-2">
                {toc.map((item, index) => (
                  <li key={item.id}>
                    <a href={`#${item.id}`} className="text-muted-foreground underline-offset-2 hover:text-foreground hover:underline">
                      {index + 1}. {item.heading}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          )}
        </header>

        <div className="space-y-10">{children}</div>
      </article>
    </LegalChrome>
  );
}

export function LegalSection({ id, heading, children }: { id: string; heading: string; children: ReactNode }) {
  return (
    <section id={id} aria-labelledby={`${id}-heading`} className="scroll-mt-24">
      <h2 id={`${id}-heading`} className="font-display text-2xl">
        {heading}
      </h2>
      {children}
    </section>
  );
}

export function LegalSubheading({ children }: { children: ReactNode }) {
  return <h3 className="mt-6 text-lg font-semibold text-foreground">{children}</h3>;
}

export function P({ children }: { children: ReactNode }) {
  return <p className="mt-3 leading-relaxed text-muted-foreground">{children}</p>;
}

export function List({ items }: { items: ReactNode[] }) {
  return (
    <ul className="mt-3 list-disc space-y-2 pl-5 leading-relaxed text-muted-foreground marker:text-muted-foreground/70">
      {items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ul>
  );
}

export function LegalTable({
  caption,
  head,
  rows,
  columnClassNames = [],
}: {
  caption: string;
  head: string[];
  rows: ReactNode[][];
  /** Optional extra classes per column, e.g. a wider minimum width for a description column. */
  columnClassNames?: string[];
}) {
  return (
    <div className="mt-4 rounded-lg border border-border">
      <Table>
        <caption className="sr-only">{caption}</caption>
        <TableHeader>
          <TableRow>
            {head.map((cell) => (
              <TableHead key={cell} className="whitespace-nowrap text-foreground">
                {cell}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, rowIndex) => (
            <TableRow key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <TableCell
                  key={cellIndex}
                  className={cn("min-w-[9rem] align-top leading-relaxed text-muted-foreground", columnClassNames[cellIndex])}
                >
                  {cell}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

const LINK_CLASS = "font-medium text-primary underline underline-offset-2 hover:opacity-80";

/** Inline link styled for legal copy: in-app paths use the router, the rest open normally. */
export function A({ href, children }: { href: string; children: ReactNode }) {
  if (href.startsWith("/")) {
    return (
      <Link to={href} className={LINK_CLASS}>
        {children}
      </Link>
    );
  }
  const external = /^https?:\/\//.test(href);
  return (
    <a href={href} className={LINK_CLASS} {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
      {children}
    </a>
  );
}

export function ContactEmail() {
  return <A href={`mailto:${LEGAL_ENTITY.contactEmail}`}>{LEGAL_ENTITY.contactEmail}</A>;
}
